/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { login } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { TOKENS } from '../../../components/design-system'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email inválido')
    .min(3, 'Mínimo 3 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('Email é obrigatório'),
  password: Yup.string()
    .min(6, 'Mínimo 6 caracteres')
    .max(50, 'Máximo 50 caracteres')
    .required('Senha é obrigatória'),
})

const initialValues = {
  email: '',
  password: '',
}

const FieldError = ({ message }: { message?: string }) => {
  if (!message) return null
  return (
    <div style={{ fontSize: 11, color: TOKENS.color.severity.high.text, marginTop: 4 }}>
      {message}
    </div>
  )
}

export function Login() {
  const { saveAuth, setCurrentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [passwordType, setPasswordType] = useState<'password' | 'text'>('password')

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await login(values.email, values.password)
        saveAuth(auth)
        setCurrentUser(auth.user)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('Login incorreto')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const baseInput: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px 10px 38px',
    fontSize: 14,
    border: `1px solid ${TOKENS.color.border}`,
    borderRadius: TOKENS.radius.card,
    background: TOKENS.color.card,
    color: TOKENS.color.text,
    outline: 'none',
    transition: 'border 80ms ease',
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
      style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}
    >
      <div style={{ textAlign: 'center', marginBottom: TOKENS.spacing.lg }}>
        <div style={{
          fontSize: TOKENS.typography.label.size,
          fontWeight: TOKENS.typography.label.weight,
          textTransform: TOKENS.typography.label.transform,
          letterSpacing: TOKENS.typography.label.tracking,
          color: TOKENS.color.muted,
          marginBottom: 6,
        }}>
          CMB · Plataforma WEB
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: TOKENS.color.text }}>
          Entrar na plataforma
        </div>
      </div>

      {formik.status && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: TOKENS.spacing.sm,
          background: TOKENS.color.severity.high.bg,
          color: TOKENS.color.severity.high.text,
          border: `1px solid ${TOKENS.color.severity.high.text}33`,
          borderRadius: TOKENS.radius.card,
          padding: '10px 12px',
          fontSize: 13,
          fontWeight: 600,
        }}>
          <AlertCircle size={16} />
          {formik.status}
        </div>
      )}

      <div>
        <div style={{ position: 'relative' }}>
          <Mail size={16} color={TOKENS.color.muted} style={{ position: 'absolute', left: 12, top: 13, pointerEvents: 'none' }} />
          <input
            placeholder='Email'
            {...formik.getFieldProps('email')}
            type='email'
            name='email'
            autoComplete='off'
            style={baseInput}
          />
        </div>
        <FieldError message={formik.touched.email && formik.errors.email ? formik.errors.email : undefined} />
      </div>

      <div>
        <div style={{ position: 'relative' }}>
          <Lock size={16} color={TOKENS.color.muted} style={{ position: 'absolute', left: 12, top: 13, pointerEvents: 'none' }} />
          <input
            type={passwordType}
            autoComplete='off'
            id='specificPassword'
            placeholder='Senha'
            {...formik.getFieldProps('password')}
            style={{ ...baseInput, paddingRight: 38 }}
          />
          <button
            type='button'
            onClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
            aria-label={passwordType === 'password' ? 'Mostrar senha' : 'Esconder senha'}
            style={{
              position: 'absolute',
              right: 6,
              top: 6,
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              background: 'transparent',
              color: TOKENS.color.muted,
              cursor: 'pointer',
              borderRadius: TOKENS.radius.chip,
            }}
          >
            {passwordType === 'password' ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <FieldError message={formik.touched.password && formik.errors.password ? formik.errors.password : undefined} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -4 }}>
        <Link
          to='/auth/forgot-password'
          style={{ fontSize: 12, fontWeight: 600, color: TOKENS.color.action, textDecoration: 'none' }}
        >
          Esqueceu a senha?
        </Link>
      </div>

      <button
        type='submit'
        id='kt_sign_in_submit'
        disabled={formik.isSubmitting || !formik.isValid}
        style={{
          marginTop: TOKENS.spacing.sm,
          width: '100%',
          border: 'none',
          background: (formik.isSubmitting || !formik.isValid) ? TOKENS.color.border : TOKENS.color.action,
          color: (formik.isSubmitting || !formik.isValid) ? TOKENS.color.muted : '#fff',
          padding: '12px 16px',
          borderRadius: TOKENS.radius.card,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 0.3,
          cursor: (formik.isSubmitting || !formik.isValid) ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: TOKENS.spacing.sm,
        }}
      >
        {loading ? (
          <>Aguarde…<span className='spinner-border spinner-border-sm align-middle' style={{ width: 14, height: 14 }} /></>
        ) : 'Continuar'}
      </button>
    </form>
  )
}
